/**
 * هندسه‌ی میز — پورتِ TableScene.cs یونیتی.
 *
 * همه‌ی «کجا»های صحنه از این‌جا می‌آیند: جای صندلی‌ها روی بیضی، بادبزنِ دستِ
 * حریف روی میز، بادبزنِ بزرگِ دستِ خودت چسبیده به لبه‌ی پایین، اندازه‌ی کارت‌ها،
 * و پراکندگیِ شبه‌تصادفیِ کارت‌های روی هم. وب پیش از این هر کدام را با عددِ
 * ثابتِ خودش می‌کشید و همین بود که هیچ‌چیز سرِ جای یونیتی نبود.
 *
 * دستگاهِ مختصات همان یونیتی است: مبدأ وسطِ صفحه، y به بالا. لایه‌ی DOM با
 * `place()` آن را به CSS برمی‌گردانَد (y معکوس).
 */
export type Vec = { x: number; y: number };

// ---- ثابت‌های TableScene ----
export const TABLE_RADIUS_FACTOR = 0.43;
export const CARD_WIDTH_FACTOR = 0.17;     // پهنای کارتِ روی میز نسبت به شعاعِ میز
export const CARD_ASPECT = 1.45;
export const DECK_OFFSET_FACTOR = -0.28;   // دسته، سمتِ چپِ کارتِ رو

/** پیش‌فرض‌های TableTheme (…Or): وقتی تمِ فرش عددِ خودش را نداده. */
export const SEAT_RING = 0.76;
export const CHAIR_RING = 0.97;
export const SEAT_STRETCH_Y = 1;
export const PORTRAIT_ASPECT = 1.12;   // TableTheme.PortraitAspectOr

/**
 * میزِ ایستاده — کاربر، ۲۱ اکتبر ۲۰۲۶: «فرشِ عمودی روی دسکتاپ بهتر است».
 *
 * روی صفحه‌ی افقی بیضیِ میز تا ۱٫۷ پهن می‌شد: فرشِ گرد و قالیچه‌ها افقی کشیده و پرده‌های
 * شهری (نقاشیِ ایستاده‌ی گوشی) با cover بزرگ و تار. حالا آن‌جا فرش ایستاده است — پهنا/بلندی
 * TALL_RUG_ASPECT، با تصویرهای tools/desktop_rugs.py — و حلقه‌ی نشست‌ها همان بیضیِ پرتره‌ی
 * گوشی (۱٫۱۲) است، پس حریف‌های چپ و راست کنارِ لبه‌ی فرش می‌نشینند نه روی آن.
 *
 * پنجره‌ی افقیِ کوتاه (گوشیِ خوابیده) همان بیضیِ پهن را نگه می‌دارد: فرشِ ایستاده آن‌جا
 * باریکه‌ای ۲۰۰ پیکسلی می‌شد. کفِ پهنا هم تضمین است که کناره‌های مات (style.css، زیرِ
 * min-width: 521px) پشتِ میزِ شفافِ شهرها باشند.
 *
 * ورودی پیکسلِ CSS ِ ظرفِ میز است، نه واحدِ بوم: در بوم ضلعِ کوتاه همیشه ۷۲۰ است و کف‌ها
 * هیچ‌وقت نمی‌گرفتند.
 */
export const TALL_RUG_ASPECT = 0.72;
export const TALL_SEAT_ASPECT = PORTRAIT_ASPECT;
export const isTall = (cssW: number, cssH: number) => cssW > cssH && cssW >= 600 && cssH >= 420;

/** اندازه‌ی صفحه و مشتقاتش — یک بار در هر تغییرِ اندازه حساب می‌شود. */
export type Screen = {
  halfW: number;
  halfH: number;
  portrait: boolean;
  /** میزِ ایستاده‌ی صفحه‌ی افقی — فرش عمودی، نشست‌ها دورش ([[isTall]]). */
  tall: boolean;
  tableR: number;      // شعاعِ میز = MinSide × 0.43
  aspect: number;      // کِششِ افقیِ بیضی
  aspectY: number;     // کِششِ عمودیِ حلقه‌ی صندلی‌ها (فقط پرتره)
};

/** `tall`: صحنه از اندازه‌ی CSS ِ ظرف می‌دهد، چون w/h این‌جا واحدِ بوم‌اند ([[isTall]]). */
export function screen(w: number, h: number, portraitAspect = PORTRAIT_ASPECT, seatStretchY = SEAT_STRETCH_Y, tall = isTall(w, h)): Screen {
  const halfW = w / 2, halfH = h / 2;
  const tableR = Math.min(w, h) * TABLE_RADIUS_FACTOR;
  // ایستاده: بیضیِ نشست‌ها همان پرتره، و مثلِ AspectFor از کناره‌ها بیرون نمی‌زند
  const aspect = tall ? Math.min(TALL_SEAT_ASPECT, (halfW * 0.96) / tableR) : aspectFor(halfW, halfH, portraitAspect);
  return {
    halfW, halfH, portrait: h > w, tall, tableR, aspect,
    aspectY: aspect < 1 ? seatStretchY : 1,
  };
}

/** AspectFor: بیضیِ میز از کناره‌ها بیرون نزند (rx ≤ ۹۶٪ نیم‌عرض). */
export function aspectFor(halfW: number, halfH: number, portraitAspect = PORTRAIT_ASPECT): number {
  if (halfH <= 1) return 1.35;
  const tableR = 2 * Math.min(halfW, halfH) * TABLE_RADIUS_FACTOR;
  const maxAspect = tableR > 1 ? (halfW * 0.96) / tableR : 1.7;
  const portrait = halfW < halfH;
  const want = portrait ? portraitAspect : clamp(halfW / halfH, 1.35, 1.7);
  return clamp(Math.min(want, maxAspect), 0.9, 1.7);
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const DEG = Math.PI / 180;

/** زاویه‌ی صندلیِ i از n (درجه). صندلیِ ۰ = خودت، پایینِ میز. */
export const seatAngleDeg = (index: number, count: number) => -90 + index * (360 / Math.max(1, count));

/** جای صندلی روی بیضی. */
export function seatPos(sc: Screen, index: number, count: number, seatR: number): Vec {
  const a = seatAngleDeg(index, count) * DEG;
  return { x: Math.cos(a) * seatR * sc.aspect, y: Math.sin(a) * seatR * sc.aspectY };
}

/** جای آواتارِ صندلی — همان SeatPos با شعاعِ حلقه‌ی صندلی‌ها. */
export const seatAnchor = (sc: Screen, index: number, count: number) =>
  seatPos(sc, index, count, sc.tableR * SEAT_RING);

// ---- اندازه‌ی کارت‌ها ----
export const cardW = (sc: Screen) => sc.tableR * CARD_WIDTH_FACTOR;                 // کارتِ روی میز
export const pileCardScale = (sc: Screen) => (sc.portrait ? 1.85 : 1.3);
export const playedCardScale = (sc: Screen) => (sc.portrait ? 2.95 : 2.2);
export const playerCardScale = (sc: Screen) => (sc.portrait ? 2.95 : 2.25);
export const playerCardW = (sc: Screen) => cardW(sc) * playerCardScale(sc);        // کارتِ دستِ خودت
export const trickCardW = (sc: Screen) => cardW(sc) * 1.7;                         // کارتِ زمینِ حکم (HokmView.TrickCardW)

/** HandCardTarget: کارتِ j-اُم از دستِ حریفِ index، بادبزنِ ملایم روی میز. */
export function handCardTarget(sc: Screen, index: number, count: number, handSize: number, j: number): { pos: Vec; rot: number } {
  const angle = seatAngleDeg(index, count);
  const rad = angle * DEG;
  const cw = cardW(sc);
  const seat = seatPos(sc, index, count, sc.tableR * SEAT_RING);
  const tangent = { x: -Math.sin(rad), y: Math.cos(rad) };
  const m = j - (handSize - 1) / 2;
  return {
    pos: { x: seat.x + tangent.x * (m * cw * 0.34), y: seat.y + tangent.y * (m * cw * 0.34) },
    rot: angle + 90 - m * 7,
  };
}

/** PlayerHandTarget: بادبزنِ قوسیِ دستِ خودت، چسبیده به لبه‌ی پایین (سبکِ Hearthstone). */
/** نسبتِ نیم‌عرضی که بادبزنِ دست اجازه دارد بگیرد — همان ۹۶٪ که بیضیِ میز رعایت می‌کند. */
const HAND_EDGE = 0.96;

/**
 * گامِ افقیِ بینِ کارت‌های دست.
 *
 * تا وقتی جا هست همان ۰٫۴ عرضِ کارت است. وقتی دست بزرگ می‌شود آن‌قدر تنگ
 * می‌شود که دورترین کارت — با احتسابِ چرخشش، که جعبه‌اش را پهن‌تر می‌کند —
 * از کناره بیرون نزند. پیش‌تر گام ثابت بود و هیچ‌جا به عرضِ صفحه نگاه
 * نمی‌کرد، پس سرِ دستِ سیزده‌تایی دو کارتِ کناری از دو طرف بریده می‌شدند.
 */
function handStep(sc: Screen, handSize: number): number {
  const maxM = (handSize - 1) / 2;
  if (maxM <= 0) return 0;
  const cw = playerCardW(sc);
  const ch = cw * CARD_ASPECT;
  const rad = Math.min(24, maxM * 8) * DEG;
  const halfExtent = (cw * Math.cos(rad) + ch * Math.sin(rad)) / 2;
  const room = Math.max(0, sc.halfW * HAND_EDGE - halfExtent);
  return Math.min(cw * 0.4, room / maxM);
}

export function playerHandTarget(sc: Screen, handSize: number, j: number): { pos: Vec; rot: number } {
  const cw = playerCardW(sc);
  const ch = cw * CARD_ASPECT;
  const m = j - (handSize - 1) / 2;
  const maxM = (handSize - 1) / 2;
  const tN = maxM > 0 ? m / maxM : 0;
  const edgeRot = Math.min(24, maxM * 8);
  const arcDepth = cw * Math.min(0.63, 0.07 * maxM * maxM);
  const baseY = Math.max(ch * 0.85, arcDepth + ch * 0.58);
  return { pos: { x: m * handStep(sc, handSize), y: -sc.halfH + baseY - tN * tN * arcDepth }, rot: -tN * edgeRot };
}

/** جایی که کارتِ خودت از آن پرواز می‌کند: لبه‌ی پایین، نیم‌کارت بالاتر. */
export const myPlayOrigin = (sc: Screen): Vec => ({ x: 0, y: -sc.halfH + playerCardW(sc) * CARD_ASPECT * 0.5 });

/** TrickPos (حکم): کارتِ زمین، رو به صاحبش، روی بیضیِ کوچکِ ۰٫۲۸ شعاع. */
export function trickPos(sc: Screen, seat: number, count: number): Vec {
  const a = seatAngleDeg(seat, count) * DEG;
  return { x: Math.cos(a) * sc.tableR * 0.28 * sc.aspect, y: Math.sin(a) * sc.tableR * 0.28 };
}

/** لنگرِ دسته (پشت‌ورو) — سمتِ چپِ کارتِ رو. */
export const deckAnchor = (sc: Screen): Vec => ({ x: sc.tableR * DECK_OFFSET_FACTOR * sc.aspect, y: 0 });

/** Scatter: جابه‌جایی و چرخشِ شبه‌تصادفیِ **پایدار** برای کارتِ seed-اُمِ یک پشته. */
export function scatter(seed: number, spreadX: number, spreadY: number, rotSpread = 22): { dx: number; dy: number; rot: number } {
  const frac = (v: number) => v - Math.floor(v);
  const r1 = frac(Math.sin((seed + 1) * 12.9898) * 43758.5453);
  const r2 = frac(Math.sin((seed + 1) * 78.233) * 12543.877);
  const r3 = frac(Math.sin((seed + 1) * 39.425) * 24634.633);
  return { dx: (r1 - 0.5) * spreadX, dy: (r2 - 0.5) * spreadY, rot: (r3 - 0.5) * rotSpread };
}

/**
 * مختصاتِ یونیتی → CSS transform روی عنصری که در مرکزِ لایه لنگر شده.
 * y معکوس می‌شود (یونیتی بالا مثبت، CSS پایین مثبت) و چرخش هم — تا کارتی که
 * در یونیتی «۹۰ درجه رو به صندلیِ راست» است، این‌جا هم همان‌طور بنشیند.
 */
export function place(p: Vec, rotDeg = 0, scale = 1): string {
  return `translate(calc(-50% + ${p.x}px), calc(-50% + ${-p.y}px)) rotate(${-rotDeg}deg) scale(${scale})`;
}
